import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase-config";
import { collection, doc, getDoc, onSnapshot, query, where, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import MusicQuizGame from "./MusicQuizGame";

export const GameRounds = ({ onBack, clientObj }) => {
  const [currentRound, setCurrentRound] = useState(1);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [teamScore, setTeamScore] = useState(0); // Track team's total score
  const [userTeam, setUserTeam] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [useNewDesign, setUseNewDesign] = useState(true); // Toggle for new design
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [isTeamAdmin, setIsTeamAdmin] = useState(false);
  const [teamAnswers, setTeamAnswers] = useState({});
  const [allMembersAnswered, setAllMembersAnswered] = useState(false);
  const totalQuestions = 10;

  // New state for client-controlled game flow
  const [currentGameId, setCurrentGameId] = useState(null);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [gameStatus, setGameStatus] = useState("waiting");
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());

  // Listen to team answers in real-time for current question
  const listenToTeamAnswers = (teamId, gameId, clientId, questionIndex) => {
    // New path: users/{clientId}/games/{gameId}/teamScores/{teamId}/answers/{questionIndex}
    const answersRef = doc(
      db,
      "users", clientId,
      "games", gameId,
      "teamScores", teamId,
      "answers", questionIndex.toString()
    );

    const unsubscribe = onSnapshot(answersRef, (doc) => {
      if (doc.exists()) {
        setTeamAnswers(doc.data());
      } else {
        // No answers yet for this question
        setTeamAnswers({});
      }
    }, (error) => {
      console.error("Error listening to team answers:", error);
    });

    return unsubscribe;
  };

  // REMOVED: listenToGameState - no longer needed, replaced by client-controlled currentQuizQuestion
  // const listenToGameState = (teamId) => {
  //   const gameStateRef = doc(db, "teams", teamId, "currentGame", "gameState");
  //
  //   const unsubscribe = onSnapshot(gameStateRef, (doc) => {
  //     if (doc.exists()) {
  //       const data = doc.data();
  //       // Sync local round with Firebase round
  //       if (data.currentRound && data.currentRound !== currentRound) {
  //         setCurrentRound(data.currentRound);
  //         setSelectedAnswer(null); // Clear selection when round changes
  //       }
  //     }
  //   }, (error) => {
  //     console.error("Error listening to game state:", error);
  //   });
  //
  //   return unsubscribe;
  // };

  // Listen to team score in real-time
  const listenToTeamScore = (teamId, gameId, clientId) => {
    const teamScoreRef = doc(db, "users", clientId, "games", gameId, "teamScores", teamId);

    const unsubscribe = onSnapshot(teamScoreRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setTeamScore(data.totalScore || 0);
      }
    }, (error) => {
      console.error("Error listening to team score:", error);
    });

    return unsubscribe;
  };

  // Listen to client document for game state (currentQuizQuestion, gameStatus, currentGameId)
  const listenToClientGameState = (clientId) => {
    const clientRef = doc(db, "users", clientId);

    const unsubscribe = onSnapshot(clientRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const newCurrentGameId = data.currentGameId || null;
        const newCurrentQuizQuestion = data.currentQuizQuestion ?? 0;
        const newGameStatus = data.gameStatus || "waiting";

        console.log("Client game state updated:", { newCurrentGameId, newCurrentQuizQuestion, newGameStatus });

        // Update state
        setCurrentGameId(newCurrentGameId);
        setCurrentQuizQuestion(newCurrentQuizQuestion);
        setGameStatus(newGameStatus);

        // Update current question display based on new index
        if (questions.length > 0 && newCurrentQuizQuestion < questions.length) {
          setCurrentQuestion(questions[newCurrentQuizQuestion]);
          console.log("Displaying question index:", newCurrentQuizQuestion);
        }
      }
    }, (error) => {
      console.error("Error listening to client game state:", error);
    });

    return unsubscribe;
  };

  // Listen to client document for game control
  useEffect(() => {
    if (!clientObj?.clientId) return;

    console.log("Setting up client game state listener for:", clientObj.clientId);
    const unsubscribe = listenToClientGameState(clientObj.clientId);

    return () => {
      console.log("Cleaning up client game state listener");
      unsubscribe();
    };
  }, [clientObj?.clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to team answers for current question
  useEffect(() => {
    if (!userTeam?.id || !currentGameId || !clientObj?.clientId) {
      return;
    }

    console.log(`Setting up listener for team answers - question ${currentQuizQuestion}`);
    const unsubscribe = listenToTeamAnswers(
      userTeam.id,
      currentGameId,
      clientObj.clientId,
      currentQuizQuestion
    );

    return () => {
      console.log(`Cleaning up listener for question ${currentQuizQuestion}`);
      unsubscribe();
    };
  }, [userTeam?.id, currentGameId, clientObj?.clientId, currentQuizQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if all team members have answered
  useEffect(() => {
    if (userTeam && teamAnswers) {
      const teamMemberIds = userTeam.teamMembers.map(member => member.id);
      const answeredMemberIds = Object.keys(teamAnswers);

      // Check if all team members have answered
      const allAnswered = teamMemberIds.every(memberId =>
        answeredMemberIds.includes(memberId)
      );

      setAllMembersAnswered(allAnswered);
    }
  }, [userTeam, teamAnswers]);

  // Update team member's answer in Firebase
  const updateTeamMemberAnswer = async (answerIndex) => {
    if (!userTeam?.id || !auth.currentUser || !currentGameId || !clientObj?.clientId) return;

    try {
      // New path: users/{clientId}/games/{gameId}/teamScores/{teamId}/answers/{questionIndex}
      const answersRef = doc(
        db,
        "users", clientObj.clientId,
        "games", currentGameId,
        "teamScores", userTeam.id,
        "answers", currentQuizQuestion.toString()
      );

      const memberData = {
        gameId: currentGameId,
        questionIndex: currentQuizQuestion,
        timestamp: serverTimestamp(),
        [`${auth.currentUser.uid}`]: {
          answer: answerIndex,
          displayName: auth.currentUser.displayName || auth.currentUser.email,
          photoURL: auth.currentUser.photoURL,
          answeredAt: serverTimestamp(),
          role: isTeamAdmin ? 'admin' : 'member'
        }
      };

      // Always merge to preserve other team members' answers
      await setDoc(answersRef, memberData, { merge: true });

      // Mark that user has answered this question
      setAnsweredQuestions(prev => new Set([...prev, currentQuizQuestion]));

      console.log(`Answer submitted for question ${currentQuizQuestion}, gameId: ${currentGameId}`);
    } catch (error) {
      console.error("Error updating team member answer:", error);
    }
  };

  // Fetch team data from root teams collection
  useEffect(() => {
    if (!auth.currentUser || !clientObj?.clientId) {
      setLoadingTeam(false);
      return;
    }

    const fetchTeams = async () => {
      setLoadingTeam(true);
      
      // First fetch the actual gameId from the client's document
      let gameId = "default";
      try {
        const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          gameId = clientData.currentGameId || "default";
          console.log("GameRounds - Fetched currentGameId from client:", gameId);
        } else {
          console.warn("GameRounds - Client document not found, using default gameId");
        }
      } catch (error) {
        console.error("GameRounds - Error fetching client document:", error);
      }
      
      // Query teams from root collection filtered by gameId
      const teamsRef = collection(db, "teams");
      const teamsQuery = query(teamsRef, where("gameId", "==", gameId));
      
      const unsubscribe = onSnapshot(teamsQuery, (snapshot) => {
        let userTeamFound = null;
        let isAdmin = false;
        
        // Find team where current user is a member
        snapshot.forEach((doc) => {
          const teamData = doc.data();
          if (teamData.teamMembers && 
              teamData.teamMembers.some(member => member.id === auth.currentUser.uid)) {
            userTeamFound = {
              id: doc.id,
              ...teamData
            };
            
            // Check if current user is the team admin (creator)
            const currentUserMember = teamData.teamMembers.find(
              member => member.id === auth.currentUser.uid
            );
            isAdmin = currentUserMember?.role === 'admin' || 
                     teamData.createdBy === auth.currentUser.uid;
          }
        });
        
        setUserTeam(userTeamFound);
        setIsTeamAdmin(isAdmin);
        setLoadingTeam(false);

        // Team answers listener moved to separate useEffect (depends on currentQuizQuestion)
        // Listen to team score
        if (userTeamFound?.id && clientObj?.clientId && gameId !== "default") {
          listenToTeamScore(userTeamFound.id, gameId, clientObj.clientId);
        }

        // Initialize game state if admin
        if (isAdmin) {
          const gameStateRef = doc(db, "teams", userTeamFound.id, "currentGame", "gameState");

          // Always mark admin as joined when they enter the game (even if rejoining)
          setDoc(gameStateRef, {
            adminJoined: true,
            adminLastJoinedAt: serverTimestamp()
          }, { merge: true }).then(() => {
            console.log("Admin marked as joined in game");
          });

          // Then check if needs initialization
          getDoc(gameStateRef).then(async docSnap => {
            if (!docSnap.exists() || !docSnap.data().currentRound) {
              // Initialize new game state if it doesn't exist or is incomplete
              await setDoc(gameStateRef, {
                currentRound: 1,
                adminJoined: true,
                lastUpdated: serverTimestamp()
              }, { merge: true });

              // Reset game scores in games collection for new game
              if (clientObj?.clientId) {
                try {
                  const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
                  if (clientDoc.exists()) {
                    const clientData = clientDoc.data();
                    const gameId = clientData.gameId || clientData.game_id || "default";

                    // Initialize team scores in games collection
                    const teamScoreRef = doc(db, "users", clientObj.clientId, "games", gameId, "teamScores", userTeamFound.id);
                    await setDoc(teamScoreRef, {
                      teamName: userTeamFound.teamName,
                      teamId: userTeamFound.id,
                      totalScore: 0,
                      members: {},
                      gameCompleted: false,
                      startedAt: serverTimestamp()
                    });
                  }
                } catch (error) {
                  console.error("Error initializing game scores:", error);
                }
              }
            }
          });
        }
      }, (error) => {
        console.error("Error fetching teams:", error);
        setUserTeam(null);
        setIsTeamAdmin(false);
        setLoadingTeam(false);
      });

      return unsubscribe;
    };

    fetchTeams();
  }, [clientObj]);

  // Fetch quiz questions from games document
  useEffect(() => {
    const fetchQuestions = async () => {
      setLoadingQuestions(true);
      try {
        let gameId = "default";
        
        // Get gameId from client document
        if (clientObj?.clientId) {
          try {
            const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
            if (clientDoc.exists()) {
              const clientData = clientDoc.data();
              gameId = clientData.currentGameId || "default";
              console.log("GameRounds - Using currentGameId for questions:", gameId);
            }
          } catch (error) {
            console.error("Error fetching client document for questions:", error);
          }
        }
        
        // Get questions from games document - these should already be set by the game host
        if (clientObj?.clientId && gameId !== "default") {
          try {
            const gamesDocRef = doc(db, "users", clientObj.clientId, "games", gameId);
            const gamesDoc = await getDoc(gamesDocRef);
            
            if (gamesDoc.exists()) {
              const gamesData = gamesDoc.data();
              if (gamesData.questions && gamesData.questions.length > 0) {
                // Use questions exactly as they are stored - no randomization
                const gameQuestions = gamesData.questions;
                console.log(`Found ${gameQuestions.length} game questions`);
                
                // Use up to totalQuestions (10) from the stored questions
                const questionsToUse = gameQuestions.slice(0, Math.min(totalQuestions, gameQuestions.length));
                
                setQuestions(questionsToUse);
                setCurrentQuestion(questionsToUse[0]);
                console.log(`Using ${questionsToUse.length} questions for this game`);
              } else {
                console.error("No questions found in game document! Game should have pre-set questions.");
                setQuestions([]);
              }
            } else {
              console.error("Game document not found!");
              setQuestions([]);
            }
          } catch (error) {
            console.error("Error fetching game questions:", error);
            setQuestions([]);
          }
        } else {
          console.error("Unable to fetch game questions - missing clientId or gameId");
          setQuestions([]);
        }
      } catch (error) {
        console.error("Error in fetchQuestions:", error);
      } finally {
        setLoadingQuestions(false);
      }
    };

    fetchQuestions();
  }, [clientObj]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update current question when currentQuizQuestion changes (controlled by client/host)
  useEffect(() => {
    if (questions.length > 0 && currentQuizQuestion < questions.length) {
      setCurrentQuestion(questions[currentQuizQuestion]);
      console.log(`Question changed to index ${currentQuizQuestion}`);

      // Don't auto-clear selectedAnswer here - let checkUserAnswerStatus handle it
    }
  }, [currentQuizQuestion, questions]);

  // Check if user has answered the current question
  useEffect(() => {
    const checkUserAnswerStatus = async () => {
      if (!userTeam?.id || !auth.currentUser || !currentGameId || !clientObj?.clientId) return;

      try {
        // New path: users/{clientId}/games/{gameId}/teamScores/{teamId}/answers/{questionIndex}
        const answersRef = doc(
          db,
          "users", clientObj.clientId,
          "games", currentGameId,
          "teamScores", userTeam.id,
          "answers", currentQuizQuestion.toString()
        );
        const answersDoc = await getDoc(answersRef);

        if (answersDoc.exists()) {
          const data = answersDoc.data();
          const userAnswer = data[auth.currentUser.uid];

          if (userAnswer) {
            // User has answered current question
            setAnsweredQuestions(prev => new Set([...prev, currentQuizQuestion]));
            setSelectedAnswer(userAnswer.answer);
            console.log(`User has already answered question ${currentQuizQuestion}`);
          } else {
            // Current question not answered yet by this user
            setSelectedAnswer(null);
          }
        } else {
          // No answers document yet for this question
          setSelectedAnswer(null);
          console.log(`No answers yet for question ${currentQuizQuestion}`);
        }
      } catch (error) {
        console.error("Error checking user answer status:", error);
      }
    };

    checkUserAnswerStatus();
  }, [currentQuizQuestion, userTeam?.id, currentGameId, clientObj?.clientId]);
  
  // REMOVED: clearTeamAnswers - no longer needed since each question has its own document
  // Answers now persist permanently in: users/{clientId}/games/{gameId}/teamScores/{teamId}/answers/{questionIndex}
  // const clearTeamAnswers = async (teamId) => {
  //   try {
  //     const gameRef = doc(db, "teams", teamId, "currentGame", "answers");
  //     await setDoc(gameRef, {});
  //   } catch (error) {
  //     console.error("Error clearing team answers:", error);
  //   }
  // };

  // Calculate points for a team member (NOT admin) based on their answer and admin's answer
  const calculateMemberPoints = (memberAnswer, adminAnswer, correctAnswer) => {
    const memberCorrect = memberAnswer === correctAnswer;
    const adminCorrect = adminAnswer === correctAnswer;
    
    // Member scoring (not applied to admin):
    if (memberCorrect && adminCorrect) {
      return 5; // Both member and admin correct
    }
    if (memberCorrect && !adminCorrect) {
      return 3; // Member correct but admin wrong (bonus for being right when admin is wrong)
    }
    return 0; // Member wrong
  };

  // Save question history with all answers and points to games collection
  const saveQuestionHistory = async (teamId, gameId, clientId, questionData) => {
    try {
      // Structure: questionHistory/{teamId}/question_{round} for team-specific history
      const historyRef = doc(db, "users", clientId, "games", gameId, "questionHistory", teamId, `question_${currentRound}`);
      await setDoc(historyRef, {
        ...questionData,
        teamId: teamId,
        teamName: userTeam?.teamName || "Unknown Team",
        submittedAt: serverTimestamp()
      });
      console.log(`Question ${currentRound} history saved for team ${teamId}`);
    } catch (error) {
      console.error("Error saving question history:", error);
    }
  };

  // Handle submit - simplified to just submit the answer
  // The host/admin interface handles scoring and round advancement
  const handleSubmit = async () => {
    // Anyone can submit their answer
    if (selectedAnswer !== null && currentQuestion && userTeam?.id && clientObj?.clientId) {
      // Submit the answer using updateTeamMemberAnswer
      await updateTeamMemberAnswer(selectedAnswer);
      console.log("Answer submitted successfully");
    }
  };

  // REMOVED: Old admin-controlled handleSubmit with scoring logic
  // This logic should be moved to a separate host/admin interface
  const handleSubmitOLD = async () => {
    // Only admin can submit and only when all members have answered
    if (!isTeamAdmin || !allMembersAnswered) {
      console.log("Cannot submit: Admin only and all members must answer");
      return;
    }

    if (selectedAnswer !== null && currentQuestion && userTeam?.id && clientObj?.clientId) {
      // Get gameId
      let gameId = "default";
      try {
        const clientDoc = await getDoc(doc(db, "users", clientObj.clientId));
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          gameId = clientData.currentGameId || "default";
        }
      } catch (error) {
        console.error("Error fetching currentGameId:", error);
      }
      
      const correctAnswer = currentQuestion.correctAnswer;
      const adminAnswer = selectedAnswer;
      const adminCorrect = adminAnswer === correctAnswer;
      const adminPoints = adminCorrect ? 5 : 0; // Admin: 5 points if correct, 0 if wrong
      
      // Calculate points for all team members
      const memberScores = {};
      let totalRoundPoints = 0;
      
      // Process admin's answer (admin's score is the team's base score)
      memberScores[auth.currentUser.uid] = {
        displayName: auth.currentUser.displayName || auth.currentUser.email,
        answer: adminAnswer,
        isCorrect: adminCorrect,
        points: adminPoints, // This is the team's base score
        role: 'admin'
      };
      totalRoundPoints += adminPoints; // Start with admin's score as team base
      
      // Process each team member's answer (members can earn additional points)
      for (const [memberId, memberData] of Object.entries(teamAnswers)) {
        if (memberId !== auth.currentUser.uid) {
          const memberAnswer = memberData.answer;
          const memberPoints = calculateMemberPoints(memberAnswer, adminAnswer, correctAnswer);
          const memberCorrect = memberAnswer === correctAnswer;
          
          memberScores[memberId] = {
            displayName: memberData.displayName,
            answer: memberAnswer,
            isCorrect: memberCorrect,
            points: memberPoints, // Member's individual points (0, 3, or 5)
            role: 'member'
          };
          totalRoundPoints += memberPoints; // Add member's points to team total
        }
      }
      
      // Create question history record
      const questionHistory = {
        questionNumber: currentRound,
        questionText: currentQuestion.question,
        correctAnswer: correctAnswer,
        correctAnswerText: currentQuestion.options[correctAnswer],
        adminAnswer: {
          userId: auth.currentUser.uid,
          answer: adminAnswer,
          answerText: currentQuestion.options[adminAnswer],
          isCorrect: adminCorrect,
          points: adminPoints
        },
        memberAnswers: memberScores,
        totalRoundPoints: totalRoundPoints
      };
      
      // Save question history to games collection
      await saveQuestionHistory(userTeam.id, gameId, clientObj.clientId, questionHistory);
      
      // Update cumulative scores in games collection
      try {
        const teamScoreRef = doc(db, "users", clientObj.clientId, "games", gameId, "teamScores", userTeam.id);
        const teamScoreDoc = await getDoc(teamScoreRef);
        const currentTeamData = teamScoreDoc.exists() ? teamScoreDoc.data() : {
          teamName: userTeam.teamName,
          totalScore: 0,
          members: {}
        };
        
        // Update each member's cumulative score
        const updatedMembers = {};
        for (const [memberId, scoreData] of Object.entries(memberScores)) {
          const currentMemberScore = currentTeamData.members?.[memberId] || {
            totalPoints: 0,
            questionsAnswered: 0,
            correctAnswers: 0
          };
          
          updatedMembers[memberId] = {
            name: scoreData.displayName,
            role: scoreData.role,
            totalPoints: currentMemberScore.totalPoints + scoreData.points,
            questionsAnswered: currentMemberScore.questionsAnswered + 1,
            correctAnswers: currentMemberScore.correctAnswers + (scoreData.isCorrect ? 1 : 0)
          };
        }
        
        // Update team scores document in games collection
        await setDoc(teamScoreRef, {
          teamName: userTeam.teamName,
          teamId: userTeam.id,
          totalScore: (currentTeamData.totalScore || 0) + totalRoundPoints,
          members: updatedMembers,
          lastQuestionAt: serverTimestamp()
        });
        
        console.log("Scores updated in games collection - Round points:", totalRoundPoints);
      } catch (error) {
        console.error("Error updating team scores in games collection:", error);
      }
      
      // Move to next question or complete game
      if (currentRound < totalQuestions) {
        // Update round in Firebase - this will sync to all team members
        try {
          const gameStateRef = doc(db, "teams", userTeam.id, "currentGame", "gameState");
          await setDoc(gameStateRef, {
            currentRound: currentRound + 1,
            lastUpdated: serverTimestamp()
          });
          
          // Clear team answers for the next round
          const answersRef = doc(db, "teams", userTeam.id, "currentGame", "answers");
          await setDoc(answersRef, {});
        } catch (error) {
          console.error("Error updating game state:", error);
        }
      } else {
        // Game completed
        try {
          // Get final scores from games collection
          const teamScoreRef = doc(db, "users", clientObj.clientId, "games", gameId, "teamScores", userTeam.id);
          const teamScoreDoc = await getDoc(teamScoreRef);
          const finalData = teamScoreDoc.data();
          const finalScore = finalData?.totalScore || 0;
          
          console.log("Game completed! Final team score:", finalScore);
          
          // Mark game as completed in games collection
          await updateDoc(teamScoreRef, {
            gameCompleted: true,
            completedAt: serverTimestamp()
          });
          
          // Clear admin joined flag since game is completed
          if (isTeamAdmin) {
            const gameStateRef = doc(db, "teams", userTeam.id, "currentGame", "gameState");
            await setDoc(gameStateRef, {
              adminJoined: false,
              gameCompleted: true,
              completedAt: serverTimestamp()
            }, { merge: true });
          }
          
          // Optionally update team's lifetime stats in teams collection (if you want to maintain this)
          try {
            const teamRef = doc(db, "teams", userTeam.id);
            const teamDoc = await getDoc(teamRef);
            if (teamDoc.exists()) {
              const teamData = teamDoc.data();
              await updateDoc(teamRef, {
                lastGameScore: finalScore,
                totalScore: (teamData.totalScore || 0) + finalScore,
                gamesPlayed: (teamData.gamesPlayed || 0) + 1,
                lastPlayedAt: serverTimestamp()
              });
            }
          } catch (error) {
            console.log("Team lifetime stats update skipped:", error);
          }
          
          console.log("Game completed successfully!");
        } catch (error) {
          console.error("Error completing game:", error);
        }
        
        // Return to home after a delay
        setTimeout(() => {
          onBack();
        }, 2000);
      }
    }
  };

  const handleAnswer = (answer) => {
    console.log("Answer selected:", answer);
    const answerIndex = currentQuestion ? currentQuestion.options.indexOf(answer) : -1;
    setSelectedAnswer(answerIndex);
    
    // Update team member's answer in Firebase
    if (answerIndex >= 0) {
      updateTeamMemberAnswer(answerIndex);
    }
  };

  const handlePlayVideo = () => {
    console.log("Play video clicked");
  };

  const handleMenuClick = () => {
    onBack();
  };

  const handleChatClick = () => {
    console.log("Chat clicked");
  };

  // Use new MusicQuizGame component if toggle is on
  if (useNewDesign) {
    // Show loading or error message
    if (loadingQuestions) {
      return (
        <MusicQuizGame
          questionNumber={currentRound}
          totalQuestions={totalQuestions}
          score={teamScore}
          questionText="Loading questions..."
          coverImage="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop"
          answerChoices={[]}
          teamMembers={userTeam?.teamMembers || []}
          onAnswer={handleAnswer}
          onSubmit={handleSubmit}
          onPlayVideo={handlePlayVideo}
          onMenuClick={handleMenuClick}
          onChatClick={handleChatClick}
          selectedAnswer={selectedAnswer}
          isLoading={true}
        />
      );
    }
    
    // Show error if no questions found
    if (!currentQuestion || questions.length === 0) {
      return (
        <MusicQuizGame
          questionNumber={0}
          totalQuestions={totalQuestions}
          score={teamScore}
          questionText="No questions available. Please contact the game host to set up questions."
          coverImage="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop"
          answerChoices={[]}
          teamMembers={userTeam?.teamMembers || []}
          onAnswer={handleAnswer}
          onSubmit={handleSubmit}
          onPlayVideo={handlePlayVideo}
          onMenuClick={handleMenuClick}
          onChatClick={handleChatClick}
          selectedAnswer={selectedAnswer}
          isLoading={false}
        />
      );
    }
    
    return (
      <MusicQuizGame
        questionNumber={currentQuizQuestion + 1} // Display as 1-based (currentQuizQuestion is 0-indexed)
        totalQuestions={totalQuestions}
        score={teamScore}
        questionText={currentQuestion.question}
        coverImage={currentQuestion.mediaUrl || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop"}
        answerChoices={currentQuestion.options}
        teamMembers={userTeam?.teamMembers || []}
        onAnswer={handleAnswer}
        onSubmit={handleSubmit}
        onPlayVideo={handlePlayVideo}
        onMenuClick={handleMenuClick}
        onChatClick={handleChatClick}
        selectedAnswer={selectedAnswer}
        isLoading={loadingQuestions}
        isTeamAdmin={isTeamAdmin}
        teamAnswers={teamAnswers}
        currentUserId={auth.currentUser?.uid}
        allMembersAnswered={allMembersAnswered}
        hasAnsweredCurrentQuestion={answeredQuestions.has(currentQuizQuestion)}
        gameStatus={gameStatus}
        userPreviousAnswer={selectedAnswer !== null ? {
          answer: selectedAnswer,
          answerText: currentQuestion?.options[selectedAnswer]
        } : null}
      />
    );
  }

  // Original design code below
  if (loadingQuestions) {
    return (
      <div className="game-rounds-redesigned">
        <div className="game-quiz-container">
          <div className="flex items-center justify-center h-full">
            <span className="text-white">Loading questions...</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (!currentQuestion || questions.length === 0) {
    return (
      <div className="game-rounds-redesigned">
        <div className="game-quiz-container">
          <div className="flex items-center justify-center h-full">
            <span className="text-white">No questions available. Please contact the game host to set up questions.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-rounds-redesigned">
      <div className="game-quiz-container">
        {/* Header with back button */}
        <div className="quiz-header">
          <button className="menu-btn" onClick={onBack}>
            <span className="hamburger-icon">☰</span>
          </button>
          <h1 className="quiz-title">MUSIC QUIZ</h1>
          <button className="search-btn">
            <span className="search-icon">🔍</span>
          </button>
        </div>

        {/* Progress and Score Section */}
        <div className="progress-section">
          <div className="question-progress">
            <span className="question-label">QUESTION {currentRound}/{totalQuestions}</span>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${(currentRound / totalQuestions) * 100}%` }}
              />
            </div>
          </div>
          <div className="score-display">
            <span className="score-label">SCORE</span>
            <span className="score-number">{score}</span>
          </div>
        </div>

        {/* Question Text */}
        <div className="question-section">
          <h2 className="question-text">{currentQuestion.question}</h2>
        </div>

        {/* Media Player Section */}
        <div className="media-section">
          <div className="media-player">
            <img 
              src={currentQuestion.mediaUrl || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop"} 
              alt="Quiz media" 
              className="media-image"
            />
            <button className="play-button">
              <span className="play-icon">▶</span>
            </button>
          </div>
        </div>

        {/* Answer Options */}
        <div className="answers-section">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              className={`answer-button ${selectedAnswer === index ? 'selected' : ''}`}
              onClick={() => setSelectedAnswer(index)}
            >
              <span className="answer-text">{option}</span>
            </button>
          ))}
        </div>

        {/* Team Section */}
        <div className="friends-section">
          <span className="friends-label">Team</span>
          <div className="friends-avatars">
            {userTeam?.teamMembers ? (
              userTeam.teamMembers.slice(0, 4).map((member, index) => (
                <div 
                  key={member.id} 
                  className={`friend-avatar ${['pink', 'cyan', 'yellow', 'purple'][index]}`}
                  title={member.displayName}
                >
                  {member.photoURL ? (
                    <img src={member.photoURL} alt={member.displayName} />
                  ) : (
                    <div className="avatar-placeholder">
                      {(member.displayName || member.email || "?")[0].toUpperCase()}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <span className="text-white">No team members</span>
            )}
          </div>
        </div>

        {/* Submit button */}
        <button 
          className="submit-btn" 
          onClick={handleSubmit}
          disabled={selectedAnswer === null}
          style={{ 
            background: selectedAnswer === null ? '#666' : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            padding: '12px 24px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            cursor: selectedAnswer === null ? 'not-allowed' : 'pointer',
            marginTop: '20px',
            width: '100%'
          }}
        >
          {selectedAnswer === null ? 'Select an Answer' : 'Submit Answer'}
        </button>
      </div>
    </div>
  );
};